// Тюнинг (PLAN.md, B3): ползунки у полей настоящих ассетов — LevelBudgetDef,
// таблицы LootCatalog, враги — и что из этого выходит на N seed: сколько
// каждого item_id лежит на уровне и падает с врагов, держится ли дефицит
// патронов. Числа в ассет не пишутся (ADR-0023): внизу — список «было/стало»
// для переноса в инспектор руками.
//
// Состояние — только в адресе: `?budget=…&n=…&o=<оверрайды>`. Ссылка
// открывает тот же экран у второго человека.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useStore } from '../data/store';
import { ApiError, loadBudgets, loadRevision, loadSchema, simulateAssets, type RevisionInfo } from '../data/source';
import * as Plot from '@observablehq/plot';
import { Callout, CopyButton, Panel, Tile } from '../components/Ui';
import { PlotFigure, type PlotOptions } from '../components/PlotFigure';
import { num, percent } from '../lib/format';
import {
  changedFields,
  changesMarkdown,
  depthCells,
  cleanOverrides,
  decodeOverrides,
  encodeOverrides,
  fieldTitle,
  groupFields,
  specialValue,
  withOverride,
  type BudgetEntry,
  type DepthProfile,
  type ItemDistribution,
  type SchemaField,
  type TuningResult,
  type TuningSchema,
} from '../lib/tuning';

export const RUN_CHOICES = [500, 1000, 2000, 5000] as const;
const DEFAULT_RUNS = 1000;
/** Пауза после движения ползунка: 1000 seed ассетов считаются ~0.7 с. */
const DEBOUNCE_MS = 400;

function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), ms);
    return () => window.clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

const errorText = (error: unknown) => (error instanceof Error ? error.message : String(error));

function parseRuns(raw: string | null): number {
  const n = Number(raw);
  return (RUN_CHOICES as readonly number[]).includes(n) ? n : DEFAULT_RUNS;
}

// --- ползунок ---------------------------------------------------------------------

function FieldSlider({
  field,
  value,
  failed,
  onChange,
}: {
  field: SchemaField;
  value: number;
  failed: boolean;
  onChange: (value: number) => void;
}) {
  const changed = value !== field.value;
  return (
    <div className={`slider${failed ? ' failed' : ''}`} id={`field-${field.key}`}>
      <span className="title">{fieldTitle(field)}</span>
      <span className={changed ? 'readout changed' : 'readout'}>
        {specialValue(field, value) ?? num(value)}
        {changed && <span className="faint"> ← {specialValue(field, field.value) ?? num(field.value)}</span>}
      </span>
      <input
        type="range"
        min={field.min}
        max={field.max}
        step={1}
        value={value}
        aria-label={field.key}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      <span className="hint">
        <span className="mono">{field.key}</span> · {num(field.min)}…{num(field.max)}
        {field.rangeSource === 'heuristic' ? ' (границы на глаз, не из валидатора)' : ''}
        {changed && (
          <>
            {' · '}
            <button type="button" className="ghost" onClick={() => onChange(field.value)}>
              вернуть {num(field.value)}
            </button>
          </>
        )}
      </span>
    </div>
  );
}

// --- результат --------------------------------------------------------------------

function spread(d: ItemDistribution): string {
  if (d.p50 === null) return '—';
  return d.p10 === d.p90 ? num(d.p50) : `${num(d.p50)} (${num(d.p10 ?? 0)}–${num(d.p90 ?? 0)})`;
}

function ItemsTable({ result }: { result: TuningResult }) {
  if (result.items.length === 0) {
    return <div className="empty">в ответе нет разбивки по предметам</div>;
  }
  return (
    <table>
      <thead>
        <tr>
          <th>item_id</th>
          <th className="n">на уровне, медиана (p10–p90)</th>
          <th className="n">за {num(result.runs)} seed</th>
          <th className="n">забегов с ним</th>
          <th className="n">с врагов, в среднем</th>
          <th className="n">за {num(result.runs)} seed</th>
          <th className="n">забегов с дропом</th>
        </tr>
      </thead>
      <tbody>
        {result.items.map((item) => (
          <tr key={item.itemId}>
            <td className="mono">{item.itemId}</td>
            <td className="n">{spread(item.placed)}</td>
            <td className="n">{num(item.placed.total)}</td>
            <td className="n">{item.placed.presentShare === null ? '—' : percent(item.placed.presentShare)}</td>
            <td className="n">{item.dropped.mean === null ? '—' : num(item.dropped.mean, 2)}</td>
            <td className="n">{num(item.dropped.total)}</td>
            <td className="n">{item.dropped.presentShare === null ? '—' : percent(item.dropped.presentShare)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DepthMap({ profile }: { profile: DepthProfile }) {
  const options = useMemo<PlotOptions>(() => {
    const cells = depthCells(profile);
    return {
      height: 36 + profile.items.length * 26,
      marginLeft: 120,
      x: { label: 'комнат от входа →', tickFormat: (d: number) => String(d) },
      y: { label: null, domain: profile.items.map((item) => item.itemId) },
      // Цвет серии постоянный, долю несёт прозрачность: шкала цвета между
      // CSS-переменными d3 интерполировать не умеет, а хексы здесь завели бы
      // второй источник правды о палитре (см. PlotFigure).
      marks: [
        Plot.cell(cells, {
          x: 'distance',
          y: 'itemId',
          fill: 'var(--series-a)',
          fillOpacity: (c: (typeof cells)[number]) => (c.perRun > 0 ? 0.1 + 0.9 * c.share : 0.03),
          inset: 1,
          title: (c: (typeof cells)[number]) =>
            `${c.itemId}, глубина ${c.distance}: ${num(c.perRun, 2)} на забег (${percent(c.share)} предмета)`,
        }),
        Plot.text(
          cells.filter((c) => c.perRun > 0),
          {
            x: 'distance',
            y: 'itemId',
            text: (c: (typeof cells)[number]) => num(c.perRun, c.perRun >= 10 ? 0 : c.perRun >= 1 ? 1 : 2),
            fill: (c: (typeof cells)[number]) => (c.share > 0.5 ? 'var(--bg-0)' : 'var(--text)'),
            fontSize: 10,
          },
        ),
      ],
    };
  }, [profile]);

  return (
    <Panel title="На уровне по глубине" aside={<span className="faint">в клетке — штук на забег, цвет — доля предмета</span>}>
      <PlotFigure options={options} ariaLabel="Предметы на уровне по расстоянию от входа" />
      <p className="dim">
        Комнат на забег по глубине: {profile.roomsPerRun.map((value) => num(value, 1)).join(' · ')}.
        {profile.unreachableFinds > 0 && ` Находок в недостижимых комнатах: ${num(profile.unreachableFinds)}.`}
      </p>
    </Panel>
  );
}

function ResultTiles({ result }: { result: TuningResult }) {
  const { respects, respectsWithDrops, dropsBreak } = result.deficit;
  const tone = (share: number | null) => (share === null ? 'plain' : share >= 0.95 ? 'good' : share >= 0.8 ? 'warn' : 'bad');
  return (
    <div className="tiles">
      <Tile label="прогонов" value={num(result.runs)} note={result.cacheHit ? 'из кэша' : `${num(result.durationMs ?? 0)} мс`} />
      <Tile
        label="дефицит патронов держится"
        value={respects === null ? '—' : percent(respects)}
        tone={tone(respects)}
        note="без патронов с трупов"
      />
      <Tile
        label="держится с дропом"
        value={respectsWithDrops === null ? '—' : percent(respectsWithDrops)}
        tone={tone(respectsWithDrops)}
        note="считая патроны с трупов"
      />
      <Tile
        label="дроп ломает дефицит"
        value={percent(dropsBreak)}
        tone={dropsBreak > 0.2 ? 'bad' : dropsBreak > 0.05 ? 'warn' : 'good'}
        note="drops_break_deficit"
      />
    </div>
  );
}

// --- страница ---------------------------------------------------------------------

export function Tuning() {
  const store = useStore();
  const online = store.server?.online ?? false;
  const [params, setParams] = useSearchParams();

  const [budgets, setBudgets] = useState<BudgetEntry[]>([]);
  const [revision, setRevision] = useState<RevisionInfo | null>(null);
  const [schema, setSchema] = useState<TuningSchema | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [result, setResult] = useState<TuningResult | null>(null);
  const [runError, setRunError] = useState<ApiError | Error | null>(null);
  const [pending, setPending] = useState(false);
  const [filter, setFilter] = useState('');
  const requestId = useRef(0);

  const budget = params.get('budget');
  const runs = parseRuns(params.get('n'));
  const rawOverrides = useMemo(() => decodeOverrides(params.get('o')), [params]);
  const overrides = useMemo(
    () => (schema === null ? rawOverrides : cleanOverrides(schema.fields, rawOverrides)),
    [schema, rawOverrides],
  );

  const update = (patch: Record<string, string | null>) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const [key, value] of Object.entries(patch)) {
          if (value === null || value === '') next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );

  // Список бюджетов и ревизия — один раз при живом сервере.
  useEffect(() => {
    if (!online) return;
    let alive = true;
    Promise.all([loadBudgets(), loadRevision()])
      .then(([list, info]) => {
        if (!alive) return;
        setBudgets(list);
        setRevision(info);
        setLoadError(null);
      })
      .catch((error: unknown) => alive && setLoadError(errorText(error)));
    return () => {
      alive = false;
    };
  }, [online]);

  // Бюджет по умолчанию — первый, если в адресе нет известного.
  useEffect(() => {
    if (budgets.length === 0) return;
    if (budget === null || !budgets.some((entry) => entry.name === budget)) {
      update({ budget: budgets[0]!.name, o: null });
    }
    // update не в зависимостях: он новый на каждой отрисовке, а смысл у него один.
  }, [budgets, budget]);

  // Схема полей бюджета.
  useEffect(() => {
    if (!online || budget === null || !budgets.some((entry) => entry.name === budget)) return;
    let alive = true;
    setSchema(null);
    setResult(null);
    setRunError(null);
    // Прогон прошлого бюджета, если он ещё в полёте, больше не «текущий»:
    // иначе его ответ лёг бы под шапку нового бюджета.
    requestId.current += 1;
    setPending(false);
    loadSchema(budget)
      .then((loaded) => alive && (setSchema(loaded), setLoadError(null)))
      .catch((error: unknown) => alive && setLoadError(errorText(error)));
    return () => {
      alive = false;
    };
  }, [online, budget, budgets]);

  // Прогон: после паузы в движении ползунков. Пока считается, на экране
  // остаётся прошлый результат с пометкой «устарело», а не пустота.
  const settledKey = useDebounced(`${budget}|${runs}|${encodeOverrides(overrides)}`, DEBOUNCE_MS);
  useEffect(() => {
    if (schema === null || budget === null || schema.budget !== budget) return;
    const [, , encoded] = settledKey.split('|');
    if (!settledKey.startsWith(`${budget}|${runs}|`)) return;
    const request = { budget, runs, overrides: decodeOverrides(encoded ?? '') };
    const id = ++requestId.current;
    setPending(true);
    simulateAssets(request)
      .then((next) => {
        if (id !== requestId.current) return;
        setResult(next);
        setRunError(null);
      })
      .catch((error: unknown) => {
        if (id !== requestId.current) return;
        setRunError(error instanceof Error ? error : new Error(String(error)));
      })
      .finally(() => id === requestId.current && setPending(false));
    // Зависимости — схема и устоявшийся ключ: budget/runs/overrides читаются из
    // ключа, иначе прогон уходил бы на каждый пиксель протяжки, мимо паузы.
  }, [schema, settledKey]);

  const setField = (field: SchemaField, value: number) =>
    update({ o: encodeOverrides(withOverride(overrides, field, value)) || null });

  const groups = useMemo(() => {
    if (schema === null) return [];
    const needle = filter.trim().toLowerCase();
    const visible = needle
      ? schema.fields.filter((field) =>
          [field.key, field.label, fieldTitle(field), field.itemId ?? '', field.group].some((text) =>
            text.toLowerCase().includes(needle),
          ),
        )
      : schema.fields;
    return groupFields(visible);
  }, [schema, filter]);

  const changes = useMemo(() => (schema === null ? [] : changedFields(schema.fields, overrides)), [schema, overrides]);
  const failedField = runError instanceof ApiError ? runError.field : null;
  const stale =
    result !== null &&
    (pending ||
      result.budget !== budget ||
      encodeOverrides(result.overrides) !== encodeOverrides(overrides) ||
      result.runs !== runs);

  if (!online) {
    return (
      <div className="page">
        <div className="page-head">
          <h1>Тюнинг</h1>
        </div>
        <Callout title="Нужен сервер" tone="hard">
          <p>
            Ползунки здесь двигают поля настоящих ассетов и пересчитывают уровень через spusk-sim. Без сервера
            считать нечем, а показывать старый образец под новыми числами было бы неправдой.
          </p>
        </Callout>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-head">
        <h1>Тюнинг</h1>
        <p>
          Ползунки у полей ассетов игры. Числа в ассет не пишутся: подобранное переносится в инспектор по списку
          «было → стало» внизу справа.
        </p>
      </div>

      <div className="controls tuning-bar">
        <label className="field">
          бюджет
          <select value={budget ?? ''} onChange={(event) => update({ budget: event.target.value, o: null })}>
            {budgets.map((entry) => (
              <option key={entry.name} value={entry.name} title={entry.displayName ?? ''}>
                {entry.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          seed
          <select value={runs} onChange={(event) => update({ n: event.target.value })}>
            {RUN_CHOICES.map((choice) => (
              <option key={choice} value={choice}>
                {num(choice)}
              </option>
            ))}
          </select>
        </label>
        {revision && (
          <span className="dim mono" title={revision.branch ?? ''}>
            ревизия {revision.gitRevision ?? 'неизвестна'}
            {revision.dirty && <span className="tag warn">незакоммиченные правки</span>}
          </span>
        )}
        <span className={pending ? 'tag warn' : 'tag good'}>{pending ? 'считается…' : 'готово'}</span>
        <button className="action" disabled={changes.length === 0} onClick={() => update({ o: null })}>
          вернуть всё как в ассетах
        </button>
      </div>

      {revision?.buildError && (
        <Callout title="Последняя сборка spusk-sim упала" tone="warn">
          <p>
            Ревизия {revision.buildError.gitRevision ?? '?'}: считает прежний бинарник, числа могут не отражать
            последние правки кода.
          </p>
          <pre className="mono small">{revision.buildError.logTail.split('\n').slice(-6).join('\n')}</pre>
        </Callout>
      )}
      {loadError && (
        <Callout title="Не загрузилось" tone="hard">
          <p className="mono">{loadError}</p>
        </Callout>
      )}

      <div className="tuning">
        <Panel
          title="Поля ассетов"
          aside={
            <input
              type="text"
              placeholder="найти поле или item_id"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
            />
          }
        >
          {schema === null ? (
            <div className="empty">{loadError ? 'схемы нет' : 'загрузка схемы…'}</div>
          ) : (
            groups.map(({ group, fields }, index) => {
              const touched = fields.some((field) => overrides[field.key] !== undefined || field.key === failedField);
              return (
                <details key={group} open={index === 0 || touched || filter.trim() !== ''} className="tuning-group">
                  <summary>
                    {group} <span className="faint">· {fields.length}</span>
                    {touched && <span className="tag warn">изменено</span>}
                  </summary>
                  <div className="sliders single">
                    {fields.map((field) => (
                      <FieldSlider
                        key={field.key}
                        field={field}
                        value={overrides[field.key] ?? field.value}
                        failed={field.key === failedField}
                        onChange={(value) => setField(field, value)}
                      />
                    ))}
                  </div>
                </details>
              );
            })
          )}
        </Panel>

        <div className="tuning-result">
          {runError && (
            <Callout title={failedField ? `Поле ${failedField}` : 'Прогон не прошёл'} tone="hard">
              <p className="mono">{runError.message}</p>
              {result && <p className="dim">Ниже — последний удачный прогон.</p>}
            </Callout>
          )}
          {stale && <div className="banner warn">устарело: показан прошлый результат, новый считается</div>}

          {result === null ? (
            <Panel title="Результат">
              <div className="empty">{pending ? 'первый прогон…' : 'нет результата'}</div>
            </Panel>
          ) : (
            <div className={stale ? 'stale' : undefined}>
              <ResultTiles result={result} />
              <Panel title={`Предметы за ${num(result.runs)} seed`} flush>
                <ItemsTable result={result} />
              </Panel>
              {result.byDepth && result.byDepth.items.length > 0 && <DepthMap profile={result.byDepth} />}
              {result.emptyFinds && result.emptyFinds.total > 0 && (
                <p className="dim">
                  Пустых находок (строка «ничего» или категория без таблицы): {num(result.emptyFinds.total)} за{' '}
                  {num(result.runs)} seed.
                </p>
              )}
            </div>
          )}

          <Panel
            title={`Было → стало · ${changes.length}`}
            aside={
              changes.length > 0 ? (
                <CopyButton
                  text={changesMarkdown(changes, budget ?? '', revision?.gitRevision ?? null)}
                  label="копировать markdown"
                />
              ) : undefined
            }
            flush
          >
            {changes.length === 0 ? (
              <div className="empty">всё как в ассетах</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>ассет</th>
                    <th>поле</th>
                    <th className="n">было</th>
                    <th className="n">стало</th>
                  </tr>
                </thead>
                <tbody>
                  {changes.map((row) => (
                    <tr key={row.key}>
                      <td className="mono">{row.asset}</td>
                      <td className="mono">{row.field}</td>
                      <td className="n">{num(row.was)}</td>
                      <td className="n warn">{num(row.now)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}
